import React from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { createStackNavigator } from "@react-navigation/stack";
import { SalesStackParamList } from '../types';
import Sales from '@navigation/screens/MidLevel/SalesScreen/SalesDashboard';
import SalesFunnel from '@navigation/screens/MidLevel/SalesScreen/SalesFunnel/SalesFunnel';
import ContentCreator from '@navigation/screens/MidLevel/SalesScreen/ContentCreator';
import SalesDetails from '@navigation/screens/MidLevel/SalesScreen/SalesStats';
import SalesEdit from '@navigation/screens/MidLevel/SalesScreen/ActiveCampaigns';
import Branding from '@navigation/screens/MidLevel/SalesScreen/Branding';
import SalesAdd from '@navigation/screens/MidLevel/SalesScreen/PreviousCampaigns';
import SalesAddProduct from '@navigation/screens/MidLevel/SalesScreen/SalesLeads';
import SalesMenu from '@navigation/screens/MidLevel/SalesScreen/SalesMenu';
import Media from '@navigation/screens/MidLevel/DataScreen/MediaScreen';
import { View, Platform, Modal } from "react-native";
import { useQuery } from "@tanstack/react-query";
import apis from "@api/api";
import { forwardRef, useCallback, useRef, useState } from "react";
import Forecast from "@navigation/screens/MidLevel/SalesScreen/Forecast";
import CampaignStageBuilder from "@navigation/screens/MidLevel/SalesScreen/Components/CampaignStageBuilder";
import useGlobalUser from "@hooks/getGlobalUser";
import SocialPosts from "@navigation/screens/MidLevel/SalesScreen/SocialPosts";
import BoostPost from "@navigation/screens/MidLevel/SalesScreen/BoostPost";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import CloseButton from "@components/UI/CloseButton";

const SalesStack = Platform.OS === 'web' ? createStackNavigator<SalesStackParamList>() : createNativeStackNavigator<SalesStackParamList>();

const SalesNavigator = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const builderRef = useRef(null);
  const loadingModalRef = useRef(null);
  const globalUser = useGlobalUser();
  const theme = themeSelector();

  const modalRef = useRef({
    openModal: () => setModalVisible(true),
    closeModal: () => setModalVisible(false)
  });


  const schema = {
    // TEXT TYPE
    campaign_title: { validation: { required: true }, type: "text", helpText: "Select a title for your campaign " },
    // DATE TYPE
    start_date: { validation: { required: true }, type: "dateTime", helpText: "Select a start date for your campaign ", ref: "dateTime" },
    end_date: { validation: { required: true }, type: "dateTime", helpText: "Select an end date for your campaign ", ref: "dateTime" },
    target_audience: { content: { label: "Select a Data Model" }, type: "text", helpText: "Select target audience dataset", ref: "customer_datas", multiple: true
  },
    marketing_scope: { validation: { required: true, enum: ['email campaign', 'website campaign', 'social media campaign', 'all the above'] }, helpText: 'Select the scope of your campaign', type: 'dropDown' },
    goals: { type: "text", helpText: "Establish your campaign goals", ref: "multiline" },
    description: { type: "text", helpText: "Enter a description for your campaign" },
    //MULTILINE TEXT TYPE
    // creative_concept: { type: "text", helpText: "Establish your creative concept", ref: "multiline" },
    // //NUMBER TYPE
    // budget: { type: 'currency', helpText: 'Enter a budget for your campaign' },
    // //ENSURE THAT THIS IS BULLSHIT 
    // financial_projection: { type: 'currency', helpText: 'Select a financial projection for your campaign' },
    sales_target: { type: 'number', helpText: 'Determine your sales target for your campaign', ref: 'number' },
    // DROPDOWN TYPE, ENUM NEDDS TO HAVE [] AND LISTED THINGS INSIDE WITH ,
  }

  const _closeLoading = useCallback(() => {
    loadingModalRef.current?.closeModal();
  }, [loadingModalRef])

  const _handleAddCampaign = useCallback(async (values) => {
    const stages = builderRef.current?.retrieveValues();
    //Ensure stages have been set up
    loadingModalRef.current?.openModal();
    await apis.postNewCampaign({ ...values, stages: !stages.length ? [] : stages }, globalUser)
      .then(async (campaignId: string) => {
        loadingModalRef.current?.closeModal();
    })
      .finally(() => {
        modalRef.current?.closeModal();
        // refetch();
      })
  }, [builderRef, loadingModalRef, modalRef])

  useQuery({
    queryKey: ['campaignData'],
    queryFn: async () => {
      const data = await apis.getActiveCampaigns()
      return data;
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true
  })

  return (
    <View style={{ flex: 1 }}>
      <Modal
        animationType="slide"
        transparent={true}
        visible={modalVisible}
        style={{ minHeight: "90vh", margin: "5%", padding:"2%" }}
        onRequestClose={() => {
          setModalVisible(!modalVisible);
        }}>
        <View style={{ minHeight: "90vh", margin: "5%", padding: 20 }}>
          <CloseButton right={30} handleClose={() => { try { setModalVisible(false); } catch { } }}
          />
          <CampaignStageBuilder ref={builderRef} /></View>
      </Modal>
      <SalesStack.Navigator
        screenOptions={{
          headerShown: false,
          cardStyle: {
            backgroundColor: theme ? "white" : "black",
          }
        }}
      >
        <SalesStack.Screen name={Platform.OS != 'web' ? "SalesMenu" : "SalesDashboard"}>
          {props => {
            return Platform.OS === 'web' ? <Sales ref={modalRef} {...props} /> : <SalesMenu {...props} />
          }}
        </SalesStack.Screen>
        {
          Platform.OS != 'web' &&
          <SalesStack.Screen name="SalesDashboard" options={{ title: "Sales Dashboard", }} component={Sales} />
        }
        <SalesStack.Screen 
          name="SocialPosts"
          component={SocialPosts}
        />
        <SalesStack.Screen name="Branding" component={Branding} />
        <SalesStack.Screen name="Media" component={Media} />
        <SalesStack.Screen 
          name="BoostPost"
          options={{ title: "Boost Post" }}
          component={BoostPost}
        />
        <SalesStack.Screen options={{ title: "Sales Funnel" }} name="SalesFunnel" component={SalesFunnel} />
        <SalesStack.Screen options={{ title: "Sales Stats" }} name="SalesStats" component={SalesDetails} />
        <SalesStack.Screen options={{ title: "Active Campaigns" }} name="ActiveCampaigns">
          {
            props => {
              return <SalesEdit ref={modalRef} {...props} />
            }
          }
        </SalesStack.Screen>
        <SalesStack.Screen options={{ title: "Previous Campaigns" }} name="PreviousCampaigns" component={SalesAdd} />
        <SalesStack.Screen name="Forecast" component={Forecast} />
        <SalesStack.Screen options={{ title: "Sales Leads" }} name="SalesLeads" component={SalesAddProduct} />
      </SalesStack.Navigator>
    </View>
  );
};
export { SalesStack, SalesNavigator };